import { useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useDropdownPosition } from '@/components/common/OuTree';
import './ScopePopover.css';

interface ScopePopoverProps {
  /** Texto del disparador (p. ej. «2 sites»). */
  triggerLabel: string;
  /** Clase del disparador (subrayado punteado clicable). */
  triggerClassName?: string;
  /** Encabezado del popover. */
  title?: string;
  /** Elementos a listar (nombres de sites/OUs). */
  items: string[];
  /** aria-label del disparador para accesibilidad. */
  ariaLabel?: string;
}

/**
 * Popover en portal que muestra el detalle de un alcance (los N sites de una fila). Reusa
 * `useDropdownPosition` de OuTree para mantenerse anclado al disparador y dentro del viewport,
 * y se monta en `document.body` para no quedar recortado por contenedores con overflow oculto.
 */
export function ScopePopover({ triggerLabel, triggerClassName, title, items, ariaLabel }: Readonly<ScopePopoverProps>) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const position = useDropdownPosition(triggerRef, open);

  const popoverStyle = position
    ? ({
        '--scope-pop-top': `${position.top}px`,
        '--scope-pop-left': `${position.left}px`,
        '--scope-pop-width': `${position.width}px`,
        '--scope-pop-max-height': `${position.maxHeight}px`,
      } as CSSProperties)
    : undefined;

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={triggerClassName ?? 'scope-pop-trigger'}
        aria-label={ariaLabel ?? triggerLabel}
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        {triggerLabel}
      </button>
      {open && position && createPortal(
        <>
          <div className="scope-pop-scrim" onClick={() => setOpen(false)} />
          <div className="scope-pop" style={popoverStyle} role="dialog" aria-label={title ?? triggerLabel}>
            {title && <div className="scope-pop-title">{title}</div>}
            <ul className="scope-pop-list">
              {items.map((name) => (
                <li key={name} className="scope-pop-item">{name}</li>
              ))}
            </ul>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

export default ScopePopover;
